import type { ReactNode } from 'react'
import type { CarReport } from '../api/types'
import { Icon, type IconName } from '../components/Icon'
import { formatDate, formatElapsed, formatNumber, isPast } from '../domain/format'
import { isPremiumTile } from '../domain/premium'

type Tone = 'neutral' | 'positive' | 'warning' | 'negative'

interface Tile {
  icon: IconName
  label: string
  value: string
  hint?: ReactNode
  tone: Tone
  /**
   * The card further down that this tile summarises, as an anchor.
   *
   * A tile answers a question in three words and the card answers it properly, so the tile is where
   * a reader wants to click. Only set where such a card actually renders: "Terugroepacties: Geen"
   * has no section to jump to, and a link that scrolls nowhere is worse than no link, because the
   * browser gives no sign that anything was meant to happen.
   *
   * The values are `SectionSpec` ids in `ReportView`. Renaming one there breaks these silently.
   */
  href?: string
}

/**
 * "In één oogopslag" — the tiles every report opens with, up to nine.
 *
 * These answer the questions somebody standing next to a second-hand car actually has, in the
 * order they ask them: is the APK valid, how old is it, does the odometer add up, is there an open
 * recall. Everything else is detail further down the page.
 *
 * A tile appears only when the RDW published the underlying value, so the grid is shorter for an
 * old car rather than padded with "onbekend".
 */
function buildTiles(report: CarReport): Tile[] {
  const tiles: Tile[] = []
  const { registration, inspection, odometer, powertrain, recalls } = report

  // On a vehicle that has never been inspected the RDW's `vervaldatum_apk` is not an expiry: it is
  // the date the APK obligation starts. Presenting it as "APK geldig tot", on a green tile, tells a
  // buyer the car holds an APK it has never had.
  //
  // An empty history only means "never inspected" when the dataset was actually read. If it could
  // not be, the report says so in `unavailableSections`, and nothing may be inferred from silence.
  const inspections = inspection.history?.length ?? 0
  const historyKnown = !report.unavailableSections.includes('inspection.history')
  const neverInspected = inspections === 0 && historyKnown

  const apkDate = formatDate(inspection.apkExpiryDate)

  if (apkDate && neverInspected) {
    tiles.push({
      icon: 'calendar',
      label: 'APK-plicht vanaf',
      value: apkDate,
      hint: 'Nog niet gekeurd',
      tone: 'neutral',
      href: '#apk',
    })
  } else if (apkDate) {
    const expired = inspection.apkExpired ?? isPast(inspection.apkExpiryDate) ?? false
    tiles.push({
      icon: 'shield',
      label: 'APK geldig tot',
      value: apkDate,
      hint: expired ? 'Verlopen' : undefined,
      tone: expired ? 'negative' : 'positive',
      href: '#apk',
    })
  }

  if (registration.constructionYear !== undefined) {
    tiles.push({
      icon: 'calendar',
      label: 'Bouwjaar',
      value: String(registration.constructionYear),
      // The age rather than the import flag, which has a tile of its own. A year is a fact a
      // reader has to convert before it means anything; this is the conversion.
      hint: elapsedSince(registration.firstAdmission, 'oud'),
      tone: 'neutral',
      href: '#registratie',
    })
  }

  // Always shown, "Nee" included. It is one of the handful of things a used-car buyer checks
  // deliberately, so the answer being no is worth reading rather than worth inferring from a tile
  // that is not there. When it is yes, the hint carries the date the vehicle reached the
  // Netherlands, which is the follow-up question.
  tiles.push({
    icon: 'globe',
    label: 'Geïmporteerd',
    value: registration.isImported ? 'Ja' : 'Nee',
    hint: registration.isImported
      ? (
          <>
            In Nederland sinds{' '}
            <span className="quick-tile__date">{formatDate(registration.firstAdmissionInNetherlands) ?? 'onbekend'}</span>
          </>
        )
      : undefined,
    tone: 'neutral',
    href: '#registratie',
  })

  const fuels = powertrain.energySources?.map((source) => source.description).filter(Boolean)
  if (fuels?.length) {
    tiles.push({
      icon: powertrain.isExternallyChargeable ? 'bolt' : 'fuel',
      label: 'Brandstof',
      value: fuels.join(' + '),
      hint: powertrain.isHybrid ? 'Hybride' : undefined,
      tone: 'neutral',
    })
  }

  // Horsepower alone, where the table below gives "72 kW (98 pk)" in full. A tile is five words wide
  // and pk is the figure Dutch readers actually hold in their head; the kW half would make the
  // value wrap onto a second line to say the same thing twice.
  const power = powertrain.maximumPowerHp
    ? formatNumber(powertrain.maximumPowerHp, 'pk')
    : formatNumber(powertrain.maximumPowerKw, 'kW')

  if (power) {
    tiles.push({ icon: 'gauge', label: 'Vermogen', value: power, tone: 'neutral', href: '#motor' })
  }

  // Power and top speed are read as a pair, so they sit together. Both keep their own rows in the
  // tables further down; the duplication is deliberate.
  const topSpeed = formatNumber(report.technical.topSpeedKmh, 'km/u')
  if (topSpeed) {
    tiles.push({ icon: 'gauge', label: 'Topsnelheid', value: topSpeed, tone: 'neutral' })
  }

  if (odometer.judgement) {
    // "Logisch" is the RDW saying the readings form a plausible series. Anything else deserves a
    // second look, but only "Onlogisch" is an actual red flag.
    const logical = odometer.judgement.toLowerCase().startsWith('logisch')
    const illogical = odometer.judgement.toLowerCase().startsWith('onlogisch')
    tiles.push({
      icon: illogical ? 'warning' : 'check',
      label: 'Tellerstand',
      value: odometer.judgement,
      hint: odometer.lastRegistrationYear ? `Laatste registratie ${odometer.lastRegistrationYear}` : undefined,
      tone: illogical ? 'negative' : logical ? 'positive' : 'neutral',
      href: '#tellerstand',
    })
  }

  if (registration.hasOpenRecall !== undefined || recalls.length > 0) {
    const open = registration.hasOpenRecall === true
    tiles.push({
      icon: open ? 'warning' : 'wrench',
      label: 'Terugroepacties',
      value: open ? 'Openstaand' : recalls.length > 0 ? 'Afgehandeld' : 'Geen',
      hint: recalls.length > 0 ? `${recalls.length} bekend` : undefined,
      tone: open ? 'negative' : 'positive',
      // Only when there is a card to arrive at. "Geen" is the premium check coming back empty, and
      // the recalls section is not rendered at all in that case.
      href: recalls.length > 0 ? '#terugroepacties' : undefined,
    })
  }

  // **"Geen" and "Nog geen keuring" are different answers and must not be merged.** A count of zero
  // on a car that has never been inspected — a 2023 EV whose first APK is years away, an oldtimer
  // with no APK duty — is not a clean record, it is no record, so that case says so in as many words
  // and stays neutral where a clean record is green. Branch on the history, never on the count alone,
  // and say nothing at all when the history could not be read.
  const found = inspection.totalDefectsFound

  if (neverInspected) {
    tiles.push({
      icon: 'calendar',
      label: 'APK-bevindingen',
      value: 'Nog geen keuring',
      hint: 'Dit voertuig is nog niet gekeurd',
      tone: 'neutral',
      href: '#apk',
    })
  } else if (historyKnown) {
    tiles.push({
      icon: found > 0 ? 'wrench' : 'check',
      label: 'APK-bevindingen',
      value: found > 0 ? (formatNumber(found) ?? '0') : 'Geen',
      hint: `Over ${formatNumber(inspections)} ${inspections === 1 ? 'keuring' : 'keuringen'}`,
      tone: found > 0 ? 'warning' : 'positive',
      href: '#apk',
    })
  }

  return tiles
}

/** "7 jaar oud", for a tile hint, or nothing when the date is missing. */
function elapsedSince(date: string | undefined, suffix: string): string | undefined {
  const elapsed = formatElapsed(date)
  return elapsed && `${elapsed} ${suffix}`
}

export function QuickCheck({ report }: { report: CarReport }) {
  const tiles = buildTiles(report)

  if (tiles.length === 0) {
    return null
  }

  return (
    <div className="quick-check">
      {tiles.map((tile) => {
        const premium = isPremiumTile(tile.label)
        const className = `quick-tile${tile.tone === 'neutral' ? '' : ` quick-tile--${tile.tone}`}${
          premium ? ' quick-tile--premium' : ''
        }`

        const content = (
          <>
            {/* The tone stays on the icon even on a premium tile: an open recall being red matters
                more than the tile looking uniform. The padlock rides on the disc rather than in the
                tile's corner, so it costs the label no width. A Dutch compound like
                "Terugroepacties" offers no break opportunity, so it cannot wrap around a corner
                badge and runs out underneath it instead. */}
            <span className="quick-tile__icon">
              <Icon name={tile.icon} />
              {premium && (
                <span className="quick-tile__premium">
                  <Icon name="unlock" size={10} />
                  <span className="visually-hidden">Premium</span>
                </span>
              )}
            </span>
            <p className="quick-tile__label">
              {tile.label}
              <span className="quick-tile__value">{tile.value}</span>
              {tile.hint && <span className="quick-tile__hint">{tile.hint}</span>}
            </p>
          </>
        )

        // An anchor rather than a div where the tile has a card behind it. Not every tile does, and
        // that asymmetry is deliberate: the ones that link have somewhere to go, and the hover state
        // is what tells them apart. A whole grid of links, half of which did nothing, would be worse.
        return tile.href ? (
          <a className={className} href={tile.href} key={tile.label}>
            {content}
          </a>
        ) : (
          <div className={className} key={tile.label}>
            {content}
          </div>
        )
      })}
    </div>
  )
}
